import type { LayerStyles, DropShadowEffect, OuterGlowEffect, InnerGlowEffect, BevelEmbossEffect, ColorOverlayEffect, GradientOverlayEffect, StrokeEffect, SatinEffect, InnerShadowEffect, PatternOverlayEffect } from "../../core/document/LayerStyle";

export interface StylePreset {
  id: string;
  name: string;
  styles: LayerStyles;
}

export class StylesPanel {
  private container: HTMLElement;
  private gridEl: HTMLElement;
  private presets: StylePreset[] = [];
  private onApply: ((styles: LayerStyles) => void) | null = null;
  private onSave: (() => LayerStyles | null) | null = null;

  constructor() {
    this.container = document.createElement("div");
    this.container.className = "styles-panel";
    this.container.innerHTML = `
      <div class="panel-title">
        Styles
        <div class="styles-panel__actions">
          <button class="ibtn" id="style-add" title="Add style from current layer">+</button>
          <button class="ibtn" id="style-delete" title="Delete selected style">×</button>
          <button class="ibtn" id="style-import" title="Import styles">↓</button>
          <button class="ibtn" id="style-export" title="Export styles">↑</button>
        </div>
      </div>
      <div class="styles-grid" id="styles-grid"></div>
    `;
    this.gridEl = this.container.querySelector(".styles-grid")!;
    this._initDefaultPresets();
    this._bindEvents();
  }

  private _initDefaultPresets(): void {
    const linearContour = { points: [{ x: 0, y: 0 }, { x: 1, y: 1 }] };
    const rgba = (r: number, g: number, b: number, a = 255) => ({ r, g, b, a });

    this.presets = [
      {
        id: "preset-drop-shadow",
        name: "Drop Shadow",
        styles: {
          dropShadow: {
            enabled: true, blendMode: "multiply", color: rgba(0, 0, 0),
            opacity: 0.75, useGlobalLight: true, angle: 120, distance: 5,
            spread: 0, size: 5, antiAliased: true, layerKnocksOut: true,
            noise: 0, contour: linearContour,
          } as DropShadowEffect,
        },
      },
      {
        id: "preset-drop-shadow-heavy",
        name: "Heavy Drop Shadow",
        styles: {
          dropShadow: {
            enabled: true, blendMode: "multiply", color: rgba(0, 0, 0),
            opacity: 0.9, useGlobalLight: true, angle: 120, distance: 12,
            spread: 20, size: 15, antiAliased: true, layerKnocksOut: true,
            noise: 0, contour: linearContour,
          } as DropShadowEffect,
        },
      },
      {
        id: "preset-outer-glow",
        name: "Outer Glow",
        styles: {
          outerGlow: {
            enabled: true, blendMode: "screen", opacity: 0.75, noise: 0,
            fill: { kind: "color", color: rgba(255, 200, 50) },
            technique: "softer", spread: 0, size: 10, contour: linearContour,
            antiAliased: true, range: 0.5, jitter: 0,
          } as OuterGlowEffect,
        },
      },
      {
        id: "preset-inner-glow",
        name: "Inner Glow",
        styles: {
          innerGlow: {
            enabled: true, blendMode: "screen", opacity: 0.75, noise: 0,
            fill: { kind: "color", color: rgba(255, 255, 255) },
            technique: "softer", source: "edge", choke: 0, size: 10,
            contour: linearContour, antiAliased: true, range: 0.5, jitter: 0,
          } as InnerGlowEffect,
        },
      },
      {
        id: "preset-bevel-emboss",
        name: "Bevel & Emboss",
        styles: {
          bevelEmboss: {
            enabled: true, style: "inner-bevel", technique: "smooth",
            depth: 0.1, direction: "up", size: 5, soften: 0,
            useGlobalLight: true, angle: 120, altitude: 30,
            glossContour: linearContour, antiAliased: true,
            highlightBlendMode: "screen", highlightColor: rgba(255, 255, 255),
            highlightOpacity: 0.75,
            shadowBlendMode: "multiply", shadowColor: rgba(0, 0, 0),
            shadowOpacity: 0.75,
          } as BevelEmbossEffect,
        },
      },
      {
        id: "preset-pillow-emboss",
        name: "Pillow Emboss",
        styles: {
          bevelEmboss: {
            enabled: true, style: "pillow-emboss", technique: "smooth",
            depth: 0.15, direction: "up", size: 8, soften: 2,
            useGlobalLight: true, angle: 120, altitude: 30,
            glossContour: linearContour, antiAliased: true,
            highlightBlendMode: "screen", highlightColor: rgba(255, 255, 255),
            highlightOpacity: 0.75,
            shadowBlendMode: "multiply", shadowColor: rgba(0, 0, 0),
            shadowOpacity: 0.75,
          } as BevelEmbossEffect,
        },
      },
      {
        id: "preset-color-overlay",
        name: "Color Overlay",
        styles: {
          colorOverlay: {
            enabled: true, blendMode: "normal", color: rgba(128, 0, 255),
            opacity: 1,
          } as ColorOverlayEffect,
        },
      },
      {
        id: "preset-gradient-overlay",
        name: "Gradient Overlay",
        styles: {
          gradientOverlay: {
            enabled: true, blendMode: "normal", opacity: 1,
            gradient: {
              colorStops: [
                { position: 0, color: rgba(0, 100, 255) },
                { position: 1, color: rgba(255, 0, 150) },
              ],
              opacityStops: [
                { position: 0, opacity: 1 },
                { position: 1, opacity: 1 },
              ],
              smoothness: 1,
            },
            reverse: false, style: "linear", alignWithLayer: false,
            angle: 90, scale: 1,
          } as GradientOverlayEffect,
        },
      },
      {
        id: "preset-stroke",
        name: "Stroke",
        styles: {
          stroke: {
            enabled: true, size: 3, position: "outside",
            blendMode: "normal", opacity: 1, fillType: "color",
            color: rgba(0, 0, 0),
          } as StrokeEffect,
        },
      },
      {
        id: "preset-satin",
        name: "Satin",
        styles: {
          satin: {
            enabled: true, blendMode: "multiply", color: rgba(0, 0, 0),
            opacity: 0.5, angle: 19, distance: 11, size: 14,
            contour: linearContour, antiAliased: true, invert: true,
          } as SatinEffect,
        },
      },
      {
        id: "preset-inner-shadow",
        name: "Inner Shadow",
        styles: {
          innerShadow: {
            enabled: true, blendMode: "multiply", color: rgba(0, 0, 0),
            opacity: 0.75, useGlobalLight: true, angle: 120, distance: 5,
            choke: 0, size: 5, antiAliased: true, noise: 0,
            contour: linearContour,
          } as InnerShadowEffect,
        },
      },
      {
        id: "preset-chrome",
        name: "Chrome",
        styles: {
          bevelEmboss: {
            enabled: true, style: "inner-bevel", technique: "chisel-hard",
            depth: 0.2, direction: "up", size: 3, soften: 0,
            useGlobalLight: true, angle: 120, altitude: 45,
            glossContour: linearContour, antiAliased: true,
            highlightBlendMode: "screen", highlightColor: rgba(255, 255, 255),
            highlightOpacity: 1,
            shadowBlendMode: "multiply", shadowColor: rgba(0, 0, 0),
            shadowOpacity: 0.5,
          } as BevelEmbossEffect,
          gradientOverlay: {
            enabled: true, blendMode: "normal", opacity: 0.8,
            gradient: {
              colorStops: [
                { position: 0, color: rgba(200, 200, 210) },
                { position: 0.5, color: rgba(255, 255, 255) },
                { position: 1, color: rgba(180, 180, 190) },
              ],
              opacityStops: [
                { position: 0, opacity: 1 },
                { position: 1, opacity: 1 },
              ],
              smoothness: 1,
            },
            reverse: false, style: "linear", alignWithLayer: false,
            angle: 90, scale: 1,
          } as GradientOverlayEffect,
          stroke: {
            enabled: true, size: 1, position: "outside",
            blendMode: "normal", opacity: 1, fillType: "color",
            color: rgba(150, 150, 160),
          } as StrokeEffect,
        },
      },
    ];
  }

  private _bindEvents(): void {
    this.container.querySelector("#style-add")?.addEventListener("click", () => this.addFromCurrentLayer());
    this.container.querySelector("#style-delete")?.addEventListener("click", () => this.deleteSelected());
    this.container.querySelector("#style-import")?.addEventListener("click", () => this.importStyles());
    this.container.querySelector("#style-export")?.addEventListener("click", () => this.exportStyles());
  }

  setCallbacks(callbacks: {
    onApply?: (styles: LayerStyles) => void;
    onSave?: () => LayerStyles | null;
  }): void {
    this.onApply = callbacks.onApply || null;
    this.onSave = callbacks.onSave || null;
  }

  render(): void {
    this.gridEl.innerHTML = this.presets.map(p => `
      <div class="style-item" data-id="${p.id}" title="${p.name}">
        <div class="style-item__preview">${this._renderPreview(p)}</div>
        <div class="style-item__name">${p.name}</div>
      </div>
    `).join("");

    this.gridEl.querySelectorAll(".style-item").forEach(el => {
      el.addEventListener("click", () => {
        const id = (el as HTMLElement).dataset.id!;
        const preset = this.presets.find(p => p.id === id);
        if (preset && this.onApply) {
          this.onApply(preset.styles);
        }
      });
      el.addEventListener("dblclick", () => {
        const id = (el as HTMLElement).dataset.id!;
        const preset = this.presets.find(p => p.id === id);
        if (preset) {
          const newName = prompt("Rename style:", preset.name);
          if (newName) {
            preset.name = newName;
            this.render();
          }
        }
      });
    });
  }

  private _renderPreview(preset: StylePreset): string {
    const s = preset.styles;
    let bg = "linear-gradient(135deg, #444, #333)";
    let shadow = "none";
    let border = "none";

    if (s.dropShadow?.enabled) {
      const ds = s.dropShadow;
      const c = ds.color;
      shadow = `${Math.round(ds.distance)}px ${Math.round(ds.distance)}px ${Math.round(ds.size)}px rgba(${c.r},${c.g},${c.b},${ds.opacity})`;
    }
    if (s.outerGlow?.enabled) {
      const og = s.outerGlow;
      const c = og.fill.kind === "color" ? og.fill.color : { r: 255, g: 200, b: 50 };
      shadow = `0 0 ${Math.round(og.size)}px ${Math.round(og.size / 2)}px rgba(${c.r},${c.g},${c.b},${og.opacity})`;
    }
    if (s.colorOverlay?.enabled) {
      const co = s.colorOverlay;
      bg = `rgb(${co.color.r},${co.color.g},${co.color.b})`;
    }
    if (s.gradientOverlay?.enabled) {
      const go = s.gradientOverlay;
      const stops = go.gradient.colorStops.map(st => `rgb(${st.color.r},${st.color.g},${st.color.b})`).join(", ");
      bg = `linear-gradient(${go.angle}deg, ${stops})`;
    }
    if (s.stroke?.enabled) {
      const st = s.stroke;
      const c = st.color || { r: 0, g: 0, b: 0, a: 255 };
      border = `${Math.round(st.size)}px solid rgba(${c.r},${c.g},${c.b},${st.opacity})`;
    }
    if (s.bevelEmboss?.enabled) {
      bg = "linear-gradient(135deg, #888, #555)";
    }

    return `<div style="width:100%;height:100%;background:${bg};box-shadow:${shadow};border:${border};border-radius:4px;"></div>`;
  }

  addFromCurrentLayer(): void {
    if (!this.onSave) return;
    const styles = this.onSave();
    if (!styles) return;
    const name = prompt("Style name:", `Style ${this.presets.length + 1}`);
    if (!name) return;
    this.presets.push({ id: crypto.randomUUID(), name, styles });
    this.render();
  }

  deleteSelected(): void {
    const customPresets = this.presets.filter(p => !p.id.startsWith("preset-"));
    if (customPresets.length === 0) return;
    const last = customPresets[customPresets.length - 1]!;
    const idx = this.presets.indexOf(last);
    if (idx >= 0) {
      this.presets.splice(idx, 1);
      this.render();
    }
  }

  importStyles(): void {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json,.asl";
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const data = JSON.parse(reader.result as string);
          if (Array.isArray(data)) {
            const name = file.name.replace(/\.[^.]+$/, "");
            for (const item of data) {
              this.presets.push({
                id: crypto.randomUUID(),
                name: item.name || `Style ${this.presets.length + 1}`,
                styles: item.styles || {},
              });
            }
            this.render();
          }
        } catch {
          alert("Failed to import styles.");
        }
      };
      reader.readAsText(file);
    });
    input.click();
  }

  exportStyles(): void {
    const data = this.presets.map(p => ({ name: p.name, styles: p.styles }));
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pixelpa-styles.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
    this.render();
  }

  destroy(): void {
    this.container.remove();
  }
}
